import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Surface } from '@vst/beam';
import { useQuery } from '@apollo/client';

import { NetworkLink } from '@app/router/constants';
import { AemFeature } from '@app/components/AdobeExperienceFragments';
import { PageAlert } from '@app/components/Alerts/PageAlert';
import AlertSectionLayout from '@app/components/AlertSectionLayout';
import { CommonQuestions } from '@app/components/CommonQuestions';
import { MobileProfileButton } from '@app/components/MobileProfileButton';
import { PageLayout } from '@app/components/PageLayout';
import { useScreenResolution } from '@app/hooks';
import { canFetchFiberServiceSubType, getFiberServiceSubType, selectHasFiber, selectUser } from '@app/redux';
import { DisplayLocation, selectAlerts } from '@app/redux/alerts';
import { selectConfig } from '@app/redux/config';
import { resetSubTypeUpdateStatus, setModemStatus } from '@app/redux/network';
import { GET_MODEM_STATUS } from '@app/redux/network/queries-and-mutations';
import { useCommonQuestionsFeature } from '@app/utils/useCommonQuestionsFeature';

import { Platform, Query } from '@mfe/shared/schema-types';

import { SubTypeUpdateStatusAlert } from '../network-mode/SubTypeUpdateStatusAlert';
import RebootModemModal from '../reboot-modem-modal';
import NetworkArticleCard from './article-card';
import MobileAppAd from './mobile-app-ad';
import MobileHelp from './mobile-help';
import { NetworkManagementCardContainer } from './network-management-card-container';
import { RebootModemCard } from './reboot-modem-card';
import { RunDiagnosticCard } from './run-diagnostic';
import SpeedTest from './speed-test';
import { checkAlertDismissed } from '../../../utils';

import styles from './styles.module.scss';

const NetworkHome = () => {
  const { enableFiber, enableBridgeMode } = useSelector(selectConfig);
  const dispatch = useDispatch();

  const { isLarge, isExtraLarge } = useScreenResolution();
  const isMediumOrSmaller = !isLarge && !isExtraLarge;

  const {
    user: { productInstanceStatus },
  } = useSelector(selectUser);

  const hasFiber = useSelector(selectHasFiber);

  const canFetchOntMode = canFetchFiberServiceSubType({
    enableFiber,
    enableBridgeMode,
    hasFiber,
    productInstanceStatus,
  });

  const { data, loading: loadingModemStatus } = useQuery<Query>(GET_MODEM_STATUS, {
    notifyOnNetworkStatusChange: true,
    variables: { refetchData: true },
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    return () => {
      dispatch(resetSubTypeUpdateStatus());
    };
  }, [dispatch]);

  useEffect(() => {
    if (data?.getModemStatus) {
      dispatch(setModemStatus(data.getModemStatus.isOnline));
    }
  }, [data, dispatch]);

  useEffect(() => {
    if (canFetchOntMode) {
      dispatch(getFiberServiceSubType());
    }
  }, [dispatch, canFetchOntMode]);

  const { alerts: allAlerts } = useSelector(selectAlerts);

  const alerts = allAlerts.filter((alert) => alert.displayLocation === DisplayLocation.GLOBAL);

  const { platform } = useSelector(selectConfig);

  const { isSmall, isExtraSmall } = useScreenResolution();
  const isMobile = isSmall || isExtraSmall;
  const showHelpCenter = platform !== Platform.Web || isMobile;

  const overviewCommonQuestionsFeature = useCommonQuestionsFeature({
    fiber: AemFeature.NETWORK_COMMON_QUESTIONS_FIBER,
    default: AemFeature.NETWORK_COMMON_QUESTIONS,
  });

  return (
    <>
      <MobileProfileButton />
      <RebootModemModal redirectRoute={`${NetworkLink.Network}/${NetworkLink.Reboot}`} />
      <PageLayout>
        <SubTypeUpdateStatusAlert />

        {!!alerts.length && (
          <AlertSectionLayout>
            {alerts
              .filter((alert) => !checkAlertDismissed(alert.type))
              .map((alert, i) => (
                <PageAlert key={i} alert={alert} />
              ))}
          </AlertSectionLayout>
        )}

        <div className={styles['content']}>
          <div className={styles['runDiagnostics']}>
            <RunDiagnosticCard />
            {showHelpCenter && <MobileHelp />}
          </div>
          <div className={styles.commonQuestions}>
            <Surface className={styles['questions']}>
              <CommonQuestions feature={overviewCommonQuestionsFeature} />
            </Surface>
          </div>
          <div className={styles['infoContainer']}>
            <div className={styles['helpCards']}>
              <SpeedTest />
              {!hasFiber && <RebootModemCard loadingModemStatus={loadingModemStatus} />}
              <NetworkManagementCardContainer
                canFetchOntMode={canFetchOntMode}
                enableFiber={enableFiber}
                enableBridgeMode={enableBridgeMode}
                hasFiber={hasFiber}
              />
            </div>
            <NetworkArticleCard />
            {!isMediumOrSmaller && <MobileAppAd variant="blue" />}
          </div>
          {isMediumOrSmaller && <MobileAppAd variant="blue" />}
        </div>
      </PageLayout>
    </>
  );
};

export default NetworkHome;
